import { Play, Pause, Timer, Hand } from "lucide-react";
import {
  GameStatus,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";

const INTERVALS = [3, 5, 8, 10];

/**
 * CallingControls: Call Next · Mode toggle (Manual/Auto) · Interval · Pause/Resume.
 */
export default function CallingControls() {
  const mode = useBingoGameStore((s) => s.mode);
  const autoInterval = useBingoGameStore((s) => s.autoInterval);
  const status = useBingoGameStore((s) => s.status);
  const setMode = useBingoGameStore((s) => s.setMode);
  const setAutoInterval = useBingoGameStore((s) => s.setAutoInterval);
  const callNext = useBingoGameStore((s) => s.callNext);
  const pause = useBingoGameStore((s) => s.pause);
  const resume = useBingoGameStore((s) => s.resume);
  const drawLen = useBingoGameStore((s) => s.drawSequence.length);
  const drawIdx = useBingoGameStore((s) => s.drawIndex);

  const inCallable =
    status === GameStatus.IN_PROGRESS ||
    status === GameStatus.PAUSED ||
    status === GameStatus.REVIEWING;
  const canCall = inCallable && drawIdx < drawLen;
  const isPaused =
    status === GameStatus.PAUSED || status === GameStatus.REVIEWING;
  const isRunning = status === GameStatus.IN_PROGRESS;
  const canTogglePause = isPaused || isRunning;

  return (
    <div className="flex flex-col gap-3">
      {/* Call Next */}
      <button
        data-testid="host-call-next"
        type="button"
        disabled={!canCall}
        onClick={callNext}
        className="h-16 rounded-md font-semibold text-white transition-all disabled:opacity-40 hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-3 text-lg"
        style={{
          background: "linear-gradient(180deg, #1f1f1f 0%, #0e0e0e 100%)",
          border: "1px solid var(--ic-primary)",
          boxShadow: "inset 0 0 20px var(--ic-primary-glow)",
        }}
      >
        <Play
          className="w-5 h-5"
          strokeWidth={2.5}
          style={{ color: "var(--ic-primary)" }}
        />
        <span>Call Next</span>
        <span className="text-xs text-[color:var(--ic-text-muted)] tracking-wider">
          {drawIdx}/{drawLen || 90}
        </span>
      </button>

      {/* Mode toggle */}
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)] mb-1.5">
          Mode
        </div>
        <div
          data-testid="host-mode-toggle"
          className="grid grid-cols-2 gap-1 p-1 rounded-md border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-elevated)]"
        >
          <SegBtn
            testid="host-mode-manual"
            active={mode === "manual"}
            onClick={() => setMode("manual")}
            icon={<Hand className="w-3.5 h-3.5" />}
            label="Manual"
          />
          <SegBtn
            testid="host-mode-auto"
            active={mode === "auto"}
            onClick={() => setMode("auto")}
            icon={<Timer className="w-3.5 h-3.5" />}
            label="Auto"
          />
        </div>
      </div>

      {/* Interval — only meaningful in Auto */}
      <div className={mode === "auto" ? "opacity-100" : "opacity-40"}>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)] mb-1.5">
          Interval
        </div>
        <div
          data-testid="host-interval"
          className="grid grid-cols-4 gap-1 p-1 rounded-md border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-elevated)]"
        >
          {INTERVALS.map((n) => (
            <SegBtn
              key={n}
              testid={`host-interval-${n}`}
              active={autoInterval === n}
              onClick={() => setAutoInterval(n)}
              label={`${n}s`}
              disabled={mode !== "auto"}
            />
          ))}
        </div>
      </div>

      {/* Pause / Resume */}
      <button
        data-testid="host-pause-resume"
        type="button"
        disabled={!canTogglePause}
        onClick={() => (isRunning ? pause() : resume())}
        className="h-10 rounded-md text-sm border border-[color:var(--ic-border-subtle)] text-white hover:border-[color:var(--ic-border-focus)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
      >
        {isRunning ? (
          <>
            <Pause className="w-4 h-4" />
            Pause
          </>
        ) : (
          <>
            <Play className="w-4 h-4" />
            Resume
          </>
        )}
      </button>
    </div>
  );
}

function SegBtn({ testid, active, onClick, icon, label, disabled }) {
  return (
    <button
      data-testid={testid}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-8 rounded text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors disabled:cursor-not-allowed"
      style={
        active
          ? {
              background: "var(--ic-primary)",
              color: "#000",
              boxShadow: "0 0 12px var(--ic-primary-glow)",
            }
          : {
              background: "transparent",
              color: "var(--ic-text-secondary)",
            }
      }
    >
      {icon}
      {label}
    </button>
  );
}
